import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/api';

// The auth token lives here, not only in React state.
//
// Previously the Authorization header was applied in a useEffect that ran after
// login() returned. Login navigated to /dashboard immediately, the Dashboard's
// own effect fired first (child effects run before ancestors'), and its four
// requests went out unauthenticated — four 401s, silently swallowed, empty
// dashboard on every fresh sign-in. A request interceptor reading a module-scope
// value removes the ordering problem entirely.
let authToken = localStorage.getItem('token');

export const setAuthToken = (value) => {
  authToken = value || null;
  if (authToken) localStorage.setItem('token', authToken);
  else localStorage.removeItem('token');
};

axios.defaults.baseURL = getApiBaseUrl();
axios.interceptors.request.use((config) => {
  if (authToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Keep the interceptor's token in step with React state.
  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get('/api/auth/profile');
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  // Login function
  const login = async (credentials) => {
    try {
      const response = await axios.post('/api/auth/login', credentials);
      const { token: newToken, user: userData } = response.data;

      // Synchronous, so requests fired by the very next render are authenticated.
      setAuthToken(newToken);
      setToken(newToken);
      setUser(userData);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      toast.success('Registration successful! Please login.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      if (token) {
        await axios.post('/api/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAuthToken(null);
      setToken(null);
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/auth/profile', profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Change password
  const changePassword = async (passwordData) => {
    try {
      const response = await axios.put('/api/auth/change-password', passwordData);
      if (response.data.token) {
        setAuthToken(response.data.token);
        setToken(response.data.token);
      }
      toast.success('Password changed. Other sessions have been signed out.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  // Check if user has specific role
  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.user_level);
    }
    return user.user_level === roles;
  };

  // Check if user is admin
  const isAdmin = () => hasRole('admin');

  // Check if user is landlord or admin
  const isLandlordOrAdmin = () => hasRole(['admin', 'manager', 'landlord']);

  // Check if user is tenant or higher
  const isTenantOrHigher = () => hasRole(['admin', 'manager', 'landlord', 'tenant']);

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    hasRole,
    isAdmin,
    isLandlordOrAdmin,
    isTenantOrHigher,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
